import React, { useEffect, useState } from 'react';
import { Card, CardContent, CircularProgress, Grid, Typography, List, Stack, IconButton, Button, Alert, Snackbar } from "@mui/material";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import CentreEditModal from './CentreEditModal';
import centreServices from 'app/services/centre-services';

// LocationSerializer no expone `address`: expone short_address y short_zone.
const formatAddress = (location) => {
    if (!location) return '';
    return _.filter([location.short_address, location.short_zone], (p) => !!p).join(', ');
};

const CentresIndex = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsList, setItemsList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [listLoading, setListLoading] = useState(false);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [openRecordEditModal, setOpenRecordEditModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, record: null });
    const [deleting, setDeleting] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'info', text: '' });

    useEffect(() => {
        if (listLoading) {
            loadItems();
        }
    }, [listLoading]);

    useEffect(() => {
        setListLoading(true);
    }, []);

    const buildItemsParams = () => {
        let par = [];
        if (searchQuery) {
            par.push(encodeURI(`q[name_or_code_cont]=${searchQuery}`));
        }
        par.push(`page=${currentPage}`);
        par.push(`include=main_centre_zone.location`);
        return `?${_.join(par, '&')}`;
    };

    const loadItems = () => {
        centreServices.getCentres(buildItemsParams())
            .then(resp => {
                setItemsList(resp.data);
                setTotalPages(resp.meta.total_pages);
            })
            .catch(() => setSnack({ open: true, severity: 'error', text: 'No se pudieron cargar las bodegas' }))
            .finally(() => setListLoading(false));
    };

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1);
        setSearchQuery(newVal);
        setListLoading(true);
    };

    const handleRecordChange = (rec) => {
        setItemsList(_.map(itemsList, (u) => u.id === rec.id ? rec : u));
        setSnack({ open: true, severity: 'success', text: 'Bodega actualizada' });
    };

    const handleRecordAdd = (rec) => {
        setItemsList(_.concat(itemsList, rec));
        setSnack({ open: true, severity: 'success', text: 'Bodega creada' });
    };

    const deleteRecord = () => {
        const record = confirmDelete.record;
        if (!record?.id || deleting) return;
        setDeleting(true);
        centreServices.deleteCentre(record.id)
            .then(() => {
                setItemsList(_.filter(itemsList, (u) => u.id !== record.id));
                setSnack({ open: true, severity: 'success', text: 'Bodega eliminada' });
            })
            .catch(err => setSnack({
                open: true,
                severity: 'error',
                text: (err?.response?.data?.errors || ['No fue posible eliminar la bodega']).join('. ')
            }))
            .finally(() => {
                setDeleting(false);
                setConfirmDelete({ open: false, record: null });
            });
    };

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450);

    const renderItemsList = () => {
        if (!itemsList?.length) {
            return <Typography>No se encontraron bodegas</Typography>;
        }

        return (
            <List disablePadding>
                {_.map(itemsList, (u) => (
                    <Card sx={{ mb: 2 }} key={u.id}>
                        <CardContent sx={{ display: 'flex' }}>
                            <Grid container spacing={5}>
                                <Grid item xs={12} md={8}>
                                    <Div sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <WarehouseOutlinedIcon fontSize="small" color="primary" />
                                        <Typography variant="h4" color="primary" ml={1}>{u.name}</Typography>
                                        {u.code && (
                                            <Typography variant="body2" color="text.secondary" ml={1}>({u.code})</Typography>
                                        )}
                                    </Div>
                                    <Div sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PlaceOutlinedIcon fontSize="small" color="primary" />
                                        <Typography variant="body2" color="text.secondary" ml={1}>
                                            {formatAddress(u.main_centre_zone?.location) || 'Sin dirección'}
                                        </Typography>
                                    </Div>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                                        <IconButton onClick={() => { setCurrentRecordId(u.id); setOpenRecordEditModal(true); }}>
                                            <EditOutlinedIcon />
                                        </IconButton>
                                        <TrashIconButton size="medium" title="Eliminar centro" onClick={() => setConfirmDelete({ open: true, record: u })} />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
            </List>
        );
    };

    return (
        <div>
            <CentreEditModal
                recordID={currentRecordId}
                isOpen={openRecordEditModal}
                onClose={() => setOpenRecordEditModal(false)}
                onRecordChange={handleRecordChange}
                onRecordAdd={handleRecordAdd} />

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
                sx={{ mb: 2 }}>
                <Typography variant="h2">Bodegas</Typography>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Button
                        variant="contained"
                        size="small"
                        color="success"
                        sx={{ width: 100 }}
                        startIcon={<ControlPointOutlinedIcon />}
                        onClick={() => { setCurrentRecordId(null); setOpenRecordEditModal(true); }}>
                        Crear
                    </Button>
                    <Search sx={{ maxWidth: { sm: 200, md: 350 } }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Buscar"
                            inputProps={{ 'aria-label': 'search' }}
                            sx={{ background: 'white' }}
                            onChange={(e) => debounceSearchQueryChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchQueryChange(e.target.value); }}
                        />
                    </Search>
                </Stack>
            </Stack>

            <Div sx={{ display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
                <CircularProgress color="warning" />
            </Div>

            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {!listLoading && renderItemsList()}
                </Grid>
            </Grid>

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page); setListLoading(true); }} />

            <ConfirmDialog
                open={confirmDelete.open}
                title="Eliminar bodega"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar la bodega <strong>{confirmDelete.record?.name}</strong>?
                        <br />
                        No se puede eliminar si tiene pedidos, vehículos, zonas o impresoras asociadas.
                    </>
                }
                loading={deleting}
                onClose={() => setConfirmDelete({ open: false, record: null })}
                onConfirm={deleteRecord}
            />

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack(s => ({ ...s, open: false }))}
                    sx={{ width: "100%" }}>
                    {snack.text}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default CentresIndex;
